import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { FaTrash, FaPlus, FaImages, FaCloudUploadAlt, FaCrown } from "react-icons/fa";
import { GiSparkles, GiDiamondRing } from "react-icons/gi";
import { uploadMediaToServer } from "../utils/uploadMediaToServer";
import { useDispatch, useSelector } from "react-redux";
import { getAllCarouselImages } from "../redux/actions/contents";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import Loading from "../Components/global/Loading";
import useAuth from "../useAuth";
import { deleteMediaFromServer } from "../utils/deleteMediaFromServer";
import PageName from "../Components/global/PageName";

export default function CarouselManager() {
  const dispatch = useDispatch();
  const { carouselsList, loading: carouselsLoading } = useSelector(
    (state) => state?.content
  );
  const { clientInfo } = useAuth();
  const { register, handleSubmit, reset } = useForm();
  const [previewCarousel, setPreviewCarousel] = useState(null);
  const [previewIcon, setPreviewIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  useEffect(() => {
    dispatch(getAllCarouselImages(1, 100, clientInfo?.clientId));
  }, [sync, dispatch, clientInfo]);

  const onSubmit = async (data) => {
    setLoading(true);
    if (data?.image && data?.image?.[0]) {
      const carouselImageFile = data?.image?.[0];
      const iconImageFile = data?.icon?.[0];

      let mediaUrl = await uploadMediaToServer(carouselImageFile, "null");
      let iconUrl = "";

      if (iconImageFile) {
        iconUrl = await uploadMediaToServer(iconImageFile, "null");
      }

      try {
        const { data: resData } = await axios.post(
          `${server}/carousel/new`,
          null,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            params: {
              title: data.title,
              description: data.description,
              imageUrl: mediaUrl,
              iconUrl: iconUrl,
            },
          }
        );
        toast.success(resData?.message || "Carousel slide created successfully.");
        setLoading(false);
        setPreviewCarousel(null);
        setPreviewIcon(null);
        reset();
        setSync((prvState) => prvState + 1);
      } catch (e) {
        setLoading(false);
        toast.error(e?.response?.data?.message || e?.message);
      }
    }
  };

  const handleDelete = async (carouselId, imageUrl, iconIcon) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this carousel slide?"
    );
    if (!userRes) return null;

    try {
      setLoading(true);
      await deleteMediaFromServer(imageUrl);
      if (iconIcon) await deleteMediaFromServer(iconIcon);

      const { data: resData } = await axios.delete(
        `${server}/carousel/deleteById`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            imageId: carouselId,
          },
        }
      );
      toast.success(resData?.message || "Carousel slide deleted successfully.");
      setSync((prvState) => prvState + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-16 bg-[#0A0A0C] text-[#F8F5F2]">
      <PageName
        title="Carousel Manager"
        subTitle="Upload and manage hero banner slideshow items in dark mode."
      />

      <div className="max-w-7xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* ══════════════════════════════════════════
              LEFT SIDE FORM CARD (DARK GLASS)
             ══════════════════════════════════════════ */}
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 md:p-7 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)] sticky top-24">
              <div className="flex items-center gap-2.5 mb-1 text-[#C8A165]">
                <GiSparkles size={18} />
                <h3 className="text-base font-bold text-[#F8F5F2]">
                  Add New Carousel Slide
                </h3>
              </div>
              <p className="text-xs text-[#F8F5F2]/50 mb-6">
                Upload high resolution hero slider banners and details.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 text-left">
                
                {/* Banner File Upload */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1.5 flex items-center gap-1.5">
                    <FaCloudUploadAlt /> Carousel Image File
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    {...register("image", { required: true })}
                    onChange={(e) =>
                      e.target.files?.[0] &&
                      setPreviewCarousel(URL.createObjectURL(e.target.files[0]))
                    }
                    className="w-full text-xs p-2.5 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all file:mr-3 file:py-1 file:px-3 file:rounded-xl file:border-0 file:text-[0.65rem] file:font-semibold file:bg-[#C8A165]/20 file:text-[#C8A165] hover:file:bg-[#C8A165] hover:file:text-[#0A0A0C]"
                  />
                  {previewCarousel && (
                    <div className="mt-3 relative rounded-2xl border border-[#C8A165]/40 overflow-hidden group">
                      <img
                        src={previewCarousel}
                        alt="Carousel Preview"
                        className="h-36 w-full object-cover"
                      />
                      <span className="absolute bottom-2 left-2 bg-black/75 backdrop-blur-md text-[#C8A165] text-[0.55rem] font-bold px-2 py-0.5 rounded-md border border-[#C8A165]/30">
                        Preview
                      </span>
                    </div>
                  )}
                </div>

                {/* Icon Upload */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1.5 flex items-center gap-1.5">
                    <GiDiamondRing /> Icon Badge File (Optional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    {...register("icon")}
                    onChange={(e) =>
                      e.target.files?.[0] &&
                      setPreviewIcon(URL.createObjectURL(e.target.files[0]))
                    }
                    className="w-full text-xs p-2.5 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all file:mr-3 file:py-1 file:px-3 file:rounded-xl file:border-0 file:text-[0.65rem] file:font-semibold file:bg-[#C8A165]/20 file:text-[#C8A165] hover:file:bg-[#C8A165] hover:file:text-[#0A0A0C]"
                  />
                  {previewIcon && (
                    <div className="mt-2 flex items-center gap-2">
                      <img
                        src={previewIcon}
                        alt="Icon Preview"
                        className="h-10 w-10 object-cover rounded-xl border border-[#C8A165]/40"
                      />
                      <span className="text-[0.6rem] text-[#F8F5F2]/50">Badge Icon Ready</span>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="block text-xs font-semibold text-[#F8F5F2]/80 mb-1.5">
                    Slide Title
                  </label>
                  <input
                    type="text"
                    {...register("title", { required: true })}
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] focus:ring-1 focus:ring-[#C8A165]/40 placeholder:text-[#F8F5F2]/30 transition-all"
                    placeholder="e.g. Signature Royal Bridal Makeup"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-semibold text-[#F8F5F2]/80 mb-1.5">
                    Description
                  </label>
                  <textarea
                    {...register("description", { required: true })}
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] focus:ring-1 focus:ring-[#C8A165]/40 placeholder:text-[#F8F5F2]/30 transition-all"
                    rows={3}
                    placeholder="Enter bespoke slide description..."
                  />
                </div>

                {/* Liquid Gold Submit Button */}
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-xs tracking-widest uppercase shadow-lg text-[#0A0A0C] transition-all disabled:opacity-50 mt-4 cursor-pointer"
                  style={{
                    background: "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                    boxShadow: "0 6px 20px rgba(200, 161, 101, 0.25)",
                  }}
                >
                  <FaPlus size={12} /> Add Carousel Slide
                </motion.button>
              </form>
            </div>
          </div>

          {/* ══════════════════════════════════════════
              RIGHT SIDE GRID DISPLAY (DARK CARDS)
             ══════════════════════════════════════════ */}
          <div className="lg:col-span-7 xl:col-span-8">
            <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 md:p-7 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)]">
              <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#C8A165]/15">
                <div>
                  <h3 className="text-lg font-bold text-[#F8F5F2] flex items-center gap-2">
                    <FaImages className="text-[#C8A165]" /> Active Hero Carousel Slides
                  </h3>
                  <p className="text-xs text-[#F8F5F2]/50 mt-1">
                    Manage active homepage slider banners and icons.
                  </p>
                </div>
                
                <span className="bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/30 px-3 py-1 rounded-full font-bold text-xs tracking-wider">
                  Total: {carouselsList?.length || 0}
                </span>
              </div>

              {carouselsList?.length === 0 ? (
                <div className="text-center py-16 text-[#F8F5F2]/40 text-xs bg-[#16161C] rounded-2xl border border-dashed border-[#C8A165]/20 p-8">
                  <FaImages size={32} className="mx-auto mb-3 text-[#C8A165]/40 animate-pulse" />
                  No carousel slides found. Upload your first slide using the form on the left.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {carouselsList?.map((carousel, index) => (
                    <motion.div
                      key={carousel?.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      className="bg-[#16161C] rounded-2xl border border-[#C8A165]/20 overflow-hidden relative group hover:border-[#C8A165]/60 hover:shadow-[0_12px_35px_rgba(200,161,101,0.15)] transition-all duration-300 text-left"
                    >
                      <div className="relative h-48 overflow-hidden bg-[#0A0A0C]">
                        <img
                          src={carousel?.imageUrl}
                          alt={carousel?.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#16161C] via-transparent to-transparent opacity-80" />

                        {/* Delete Action Button */}
                        <button
                          onClick={() =>
                            handleDelete(
                              carousel?.id,
                              carousel?.imageUrl,
                              carousel?.iconUrl
                            )
                          }
                          className="absolute top-3 right-3 bg-red-500/20 hover:bg-red-600 border border-red-500/40 text-red-300 hover:text-white p-2.5 rounded-full backdrop-blur-md shadow-lg transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                          title="Delete Slide"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>

                      <div className="p-5 flex items-start gap-3.5 relative">
                        {carousel?.iconUrl && (
                          <img
                            src={carousel?.iconUrl}
                            alt="icon"
                            className="w-10 h-10 rounded-xl border border-[#C8A165]/30 object-cover shrink-0 bg-[#0A0A0C]"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-[#F8F5F2] group-hover:text-[#C8A165] transition-colors truncate">
                            {carousel?.title}
                          </h4>
                          <p className="text-xs text-[#F8F5F2]/60 mt-1 leading-relaxed line-clamp-3">
                            {carousel?.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {(loading || carouselsLoading) && <Loading />}
    </div>
  );
}
